import { AttributesMixin, define, Model, type } from "@type-r/models";
import { matchString } from "api/common";
import { asTimelessDate } from "lqx-react/lib";
import { AssetChange } from "./assetChange";
import { AssetEvent, AssetEventStatus } from "./assetEvent";
import { AssetType } from "./assetType";
import { restfulIO } from "./endpoint";
import { CounterParty, RemittanceAdvice } from "./remittanceAdvice";

export interface RemittanceAdviceChange extends AttributesMixin<typeof RemittanceAdviceChange> { }
@define
export class RemittanceAdviceChange extends AssetChange {
    static endpoint = restfulIO('api/v1/ingest/remittance-advices/assets');

    static attributes = {
        ...AssetChange.attributes,
        updateAsset: RemittanceAdvice,
        assetType: type(Number).value(AssetType.remittanceAdvice),
        assetEvents: type([AssetEvent]).value([]).dontSave
    };

    getAssetType() {
        return AssetType.remittanceAdvice;
    }

    getAssetName() {
        return 'Remittance Advice';
    }

    get paymentReferenceNumber(): String {
        return this.getValue("paymentReferenceNumber");
    }

    get payor(): CounterParty {
        return this.getValue("payor") || new CounterParty();
    }

    get payee(): CounterParty {
        return this.getValue("payee") || new CounterParty();
    }

    get paymentDate(): Date {
        const value = this.getValue("paymentDate");
        return value ? new Date(value) : null;
    }

    get paymentAmount(): String {
        return this.getValue("paymentAmount");
    }

    hasPendingAssetEventsFor(eventTypeId: number): boolean {
        return this.assetEvents.filter(ae => ae.status == AssetEventStatus.pending && ae.eventTypeId == eventTypeId).length > 0;
    }

    filter(filter) {
        return (matchString(this.paymentReferenceNumber.toString(), filter.search) ||
            matchString(this.payor.company.name, filter.search) ||
            matchString(this.paymentAmount.toString(), filter.search) ||
            matchString(asTimelessDate(this.paymentDate), filter.search) ||
            matchString(this.state.name, filter.search) ||
            matchString(this.payee.company.name, filter.search)) &&
            this.hasReceivedAssetEventsFor(filter.eventType);
    }
}

export interface RemittanceAdviceData extends AttributesMixin<typeof RemittanceAdviceData> { }
@define
export class RemittanceAdviceData extends Model {
    static endpoint = restfulIO('api/v1/ingest/remittance-advices/assets');

    static attributes = {
        count: Number,
        items: [RemittanceAdviceChange]
    };
}
